import  { type MetaFunction, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, json, useLoaderData, useSearchParams } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import ScriptGrid, { type ScriptLayoutType } from '#app/components/script-grid.js'
import { Combobox } from '#app/components/ui/combobox'
import { usePersistedState } from '#app/hooks/use-persisted-state'
import { prisma } from '#app/utils/db.server.js'
import { useRequestInfo } from '#app/utils/request-info.js'
import { getServerList } from '#app/utils/selected-server-options.js'

export const meta: MetaFunction = () => [{ title: brandName }]

export async function loader({ request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const category = url.searchParams.get('category')
	const tag = url.searchParams.get('tag')

	const scripts = await prisma.script.findMany({
		where: { 
			display: true, 
			creatorId: { not: null },
			...(category ? { category } : {}),
			...(tag ? { tags: { contains: tag } } : {})
		},
		orderBy: { updatedAt: "desc" },
		select: { 
			id: true, 
			slug: true, 
			server: true, 
			category: true,
			tags: true,
			title: true, 
			updatedAt: true, 
			creator: { 
				select: { username: true } 
			},
			description: true,
			reactions: {
				select: {
					type: true
				}
			}
		},
	});

	// Process reactions
	const scriptsWithReactions = scripts.map(script => {
		const reactionCounts = script.reactions.reduce((acc, reaction) => {
			acc[reaction.type] = (acc[reaction.type] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);

		const reactions = Object.entries(reactionCounts).map(([type, count]) => ({
			type,
			count
		}));

		return {
			...script,
			reactions
		};
	});

	return json({
		scripts: scriptsWithReactions,
		filter: {
			category,
			tag
		}
	});
}

export default function IndexRoute() {
	const { scripts, filter } = useLoaderData<typeof loader>()
	const [searchParams] = useSearchParams()
	const userPreference = useRequestInfo()
	const [selectedServer, setSelectedServer] = usePersistedState<string | null>('selectedServer', null)
	const serverList = getServerList()

	const filteredScripts = selectedServer
		? scripts.filter(script => script.server === selectedServer)
		: scripts

	return (
		<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
			<div className="flex flex-wrap items-center justify-center">
				<h1 className="text-center text-h2">
					{filter.category ? `Scripts in ${filter.category}` : 
					 filter.tag ? `Scripts tagged with ${filter.tag}` :
					 `UO Scripts From Users: ${filteredScripts.length}`}
				</h1>
			</div>
			<p className="my-2 text-center text-muted-foreground">
				{filter.category || filter.tag ? (
					<Link to="/scripts" className="underline hover:no-underline">← Back to all scripts</Link>
				) : (
					<>List of scripts added to the site by users like you. Find the perfect script for your needs and <Link to="/script/new" className="underline hover:no-underline">contribute your own</Link> to help others.</>
				)}
			</p>
			<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">
				<div className="w-full max-w-md mb-8">
					<Combobox
						name="server"
						value={selectedServer}
						onChange={setSelectedServer}
						options={serverList}
						label="Filter by Server"
						placeholder="Select a server"
					/>
				</div>
				<div className="flex flex-col">
					<div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
						{filteredScripts.length > 0 ? (
							filteredScripts.map(script => <ScriptGrid {...script} key={`logged_${script.slug}`} className='' />)
						) : (
							<p className="text-center col-span-full">No scripts found matching your criteria.</p>
						)}
					</div>
				</div>
			</div>
			{selectedServer && (
				<div className="m-4">Showing results based on server preference: {selectedServer}</div>
			)}
		</div>
	)
}